import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { WidgetsQuestionModel } from 'src/app/models/widgets/widgets-question.model';
import { WidgetsFormModel } from 'src/app/models/widgets/widgets-form.model';
import { WidgetsService } from 'src/app/services/widgets/widgets.service';
import { WidgetsAbstract } from 'src/app/abstract/widgets/widgets.abstract';
import { Logger } from 'src/app/core/logger';
import { WidgetsMetadataService } from 'src/app/services/widgets/widgets-metadata.service';
import { WidgetsContainerComponentAbstract } from 'src/app/abstract/widgets/widgets-container-component.abstract';
import { CdkDrag, CdkDragDrop } from '@angular/cdk/drag-drop';
import { CommandService } from 'src/app/services/command/command.service';
import { WidgetsTypeEnum } from 'src/app/enums/widgets/widgets-type.enum';
import { PermissionsService } from 'src/app/services/auth/permissions.service';
import { CommandResequenceQuestionsService } from 'src/app/services/command/command-resequence-questions.service';
import { WidgetsNotifyDeleteService } from 'src/app/services/widgets/widgets-notify-delete.service';
import { WidgetsSelectionService } from 'src/app/services/widgets/widgets-selection.service';
import { WidgetsRelatedinputModel } from 'src/app/models/widgets/widgets-related-input.model';
import { FormInformation } from 'src/app/models/api/form-information.model';
import { QuestionService } from 'src/app/services/api/question.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  // eslint-disable-next-line
  selector: '[appWidgetsQuestion]',
  templateUrl: './widgets-question.component.html',
  styleUrls: ['./widgets-question.component.scss'],
})
export class WidgetsQuestionComponent
  extends WidgetsContainerComponentAbstract
  implements OnInit
{
  @Input() data: WidgetsQuestionModel = new WidgetsQuestionModel();
  @Input() dropListIds: string[] = [];
  @Input() isInFormBuilder: boolean = true;  // true: form builder (save immediately), false: question editor (defer save)
  @Output() dropListIdAdded: EventEmitter<string> = new EventEmitter<string>();
  tempData: WidgetsQuestionModel = new WidgetsQuestionModel();
  public fields: string[] = [];
  public hidefield: string = '';
  isMarkedForReview: boolean = false;

  constructor(
    private widgetsService: WidgetsService,
    private selectionService: WidgetsSelectionService,
    private commandService: CommandService,
    private widgetsMetadataService: WidgetsMetadataService,
    private notifyDeleteService: WidgetsNotifyDeleteService,
    private commandResequenceQuestions: CommandResequenceQuestionsService,
    private questionService: QuestionService,
    private permissionsService: PermissionsService,
    private toastr: ToastrService,
    private logger: Logger
  ) {
    super();
  }

  ngOnInit(): void {
    this.refreshMetadata();

    this.onRefreshConditionalFields();
    // Assign a key to the Question based on the name
    // TODO: Is this the best place to do that?? In the command when creating the question?
    this.data.key = this.widgetsService.getNextKeyForName(this.data.name);
    this.isMarkedForReview = (this.data.isMarkedForReview) ? true : false;
  }

  /**
   * We work with a cloned copy of the layout data that we refresh here.
   */
   refreshMetadata() {
    // Use a copy of data for Metadata (we don't want to modify the layout directly)
    this.tempData.cloneFrom(this.data);
    this.tempData.id = this.data.id;    // Even use same uuid
  }

  onWidgetDroppedToCanvas(
    event: CdkDragDrop<WidgetsAbstract[], WidgetsAbstract[], any>
  ): WidgetsAbstract {
    // TODO: ROWS, SECTIONS, QUESTIONS and TEXT are instantiated here. Future: Command Pattern for Undo/Redo
    // OPTION: Move the question to the bottom Only If Section is Collapsed
    const primitive = this.commandService.execute(this.data, event.item.data);
    // @Note: primitives dropped to a question are created in edit mode
    primitive.isEdit = true;
    this.commandService.prepareForUndo();
    return primitive;
  }

  isDroppableWidget(item: CdkDrag<WidgetsAbstract>) {
    // Constraints: ROWS AND CUSTOM widgets are allowed under a FORM
    // TODO: For now, TEXT too. To be substutited by SECTION
    switch (item.data.widgetType) {
      case WidgetsTypeEnum.FIELD:
      case WidgetsTypeEnum.ROW:
      case WidgetsTypeEnum.TEXT:
      case WidgetsTypeEnum.RADIO:
      case WidgetsTypeEnum.INPUT:
      case WidgetsTypeEnum.TIME_RANGE:
      case WidgetsTypeEnum.ARRAY:
      case WidgetsTypeEnum.CHECKBOX:
      case WidgetsTypeEnum.ORDINAL_IN_SET:
        return true;
    }

    return false;
  }

  onRefreshConditionalFields(): void {
    this.fields = this.widgetsService.getAllFieldsOfType('');
  }

  onHide($event: any) {
    // TODO: Delete from here. It's in the metadata component now...
    this.data.template =
      '{ expressions: { hide: "field.parent.model.' + this.hidefield + '"}}';
  }

  onDebug() {
    const rootTree: WidgetsFormModel = this.widgetsService.getLayoutRoot();
    const customWidgetNode: WidgetsQuestionModel = this.data;
    this.logger.log('Override ' + this.data.labelOverrides.get('$label1'));
  }

  onDeleteModal(): void {
    this.notifyDeleteService.notifyDelete(this.data);
  }

  /**
   * @deprecated Use cloneFrom instead
   * @param WidgetsAbstract
   */
  clone(_widget: any): WidgetsAbstract {
    throw new Error('Method not implemented.');
  }

  onWidgetsReordered(culprits: WidgetsAbstract[]) {
    this.commandResequenceQuestions.executeAsync(this.widgetsService.getLayoutRoot(), undefined)
      .then((numQuestions) => {
        this.logger.log("onWidgetsReordered Resequenced "+numQuestions+" questions.");
      })
      .catch((error) => {
        this.logger.warn("onWidgetsReordered Error "+error);
      });
  }

  changeKey(prevKey, newKey) {
    const value = this.data.labelOverrides[prevKey];
    delete this.data.labelOverrides[prevKey];
    this.data.labelOverrides[newKey] = value;
    // console.log(this.data.labelOverrides);
  }

  changeValue(key, value) {
    this.data.labelOverrides.set(key, value);
    // console.log('changeValue: [' + key + ',' + value + '] [' + this.data.labelOverrides[key] + ']' );
    // this.refreshQuestionOverrides();
  }

  onMaximize(): void {
    this.isMinimized = false;
    this.refreshMetadata();

    // Inform Metadata View (even if it's hidden)
    this.widgetsMetadataService.showMetadata(this.data);
  }

  onMinimize(): void {
    this.isMinimized = true;
    this.refreshMetadata();
  }

  onSelect() {
    this.selectionService.toggleSelection(this.data).then(() => {
      this.widgetsService.setLayoutTouched();
      this.tempData.isSelected = this.data.isSelected;
    });
  }

  /**
   * Toggle the OnPoint flag for this question.
   * If in form builder: saves to database immediately.
   * If form is in Draft or Rejected state: saves to database immediately.
   * If in question editor: defers save until "Update to Remote" is clicked.
   */
  onToggleOnPointStar() {
    // Toggle the flag directly on data model (single source of truth)
    this.data.isOnPoint = !this.data.isOnPoint;
    this.widgetsService.setLayoutTouched();

    // Save immediately if in form builder context AND form is in Draft/Rejected state
    if (this.data.dbId && (this.isInFormBuilder && this.permissionsService.formStatusAutoSaveEnabled())) {
      this.saveQuestionToDatabase();
    }
  }

  /**
   * Save the question to the database.
   * Transform data to API format (PascalCase) before sending.
   */
  saveQuestionToDatabase() {
    if (!this.data.dbId) {
      return;  // Question must be saved to DB first
    }

    const formData = {
      Id: this.data.dbId,
      Label: this.data.name,
      TypeId: this.data.typeId,
      Namespace: this.data.key,
      Description: this.data.description,
      CreatedBy: this.data.createdBy,
      Template: this.data,  // isOnPoint is already inside the Template object
      Tags: this.data.tags
    };

    this.questionService.saveQuestion(formData).subscribe(
      (response) => {
        this.toastr.success('Question successfully updated.');
      },
      (error) => {
        this.toastr.error(error);
      }
    );
  }

  /**
   * Handle OnPoint question toggle event from metadata component
   */
  onOnPointQuestionToggled(value: boolean) {
    this.data.isOnPoint = value;
    this.widgetsService.setLayoutTouched();
  }
}
